import React, { useEffect, useMemo, useState } from "react";
import {
  toCentimetres,
  toKilograms,
  heightError,
  weightError,
} from "../../../services/bodyMeasurements";
import {
  IonButton,
  IonContent,
  IonDatetime,
  IonFooter,
  IonLoading,
  IonModal,
  IonPage,
  IonText,
  useIonViewWillLeave,
} from "@ionic/react";
import { useDispatch, useSelector } from "react-redux";
import { useHistory } from "react-router";
import { trackFunnel } from "../../../services/funnelTracker";
import {
  getBirthDateWheelMax,
  getMinimumLegalAgeDate,
} from "../../../services/functions";

import "../InitialConfiguration.min.css";
import bgImage from "../../../assets/images/logo sfondo.png";

import { setInitialConfigProperty } from "../../../redux/reducer/initialConfigSlice";
import { updateMasterClubSubscriber } from "../../../redux/actions/initialConfigActions";
import { getAuthToken } from "../../../services/authStorage";

const pad2 = (v) => String(v ?? "").padStart(2, "0");

const MONTHS_IT = [
  "gennaio",
  "febbraio",
  "marzo",
  "aprile",
  "maggio",
  "giugno",
  "luglio",
  "agosto",
  "settembre",
  "ottobre",
  "novembre",
  "dicembre",
];

const isValidDate = (yyyy, mm, dd) => {
  const y = Number(yyyy);
  const m = Number(mm);
  const d = Number(dd);
  if (!y || !m || !d) return false;
  const dt = new Date(y, m - 1, d);
  return dt.getFullYear() === y && dt.getMonth() === m - 1 && dt.getDate() === d;
};

const buildBirthDate = (dd, mm, yyyy) => {
  const y = Number(yyyy);
  const m = Number(mm);
  const d = Number(dd);
  if (!y || !m || !d) return "";
  if (!isValidDate(y, m, d)) return "";
  // ✅ API format wanted:
  return `${y}-${pad2(m)}-${pad2(d)}`;
};

const titleCase = (s) =>
  String(s ?? "")
    ? `${String(s).charAt(0).toUpperCase()}${String(s).slice(1)}`
    : "";

/** Height and weights are optional: an empty box is not an error. */
const isFilled = (v) => String(v ?? "").trim() !== "";

const PhysicalPersonalStep = () => {
  const dispatch = useDispatch();
  const history = useHistory();
  const isPendingRegistration =
    !!localStorage.getItem("adoc_rt") && !getAuthToken();

  const sex = useSelector((s) => s.initialConfig.sex);
  const height = useSelector((s) => s.initialConfig.height);
  // "current_weight" = Peso attuale (today's weight). The form below also
  // collects "initial_weight" = Peso di partenza (path baseline), which mirrors
  // Peso attuale until the member types in it.
  const current_weight = useSelector((s) => s.initialConfig.current_weight);
  const initial_weight = useSelector((s) => s.initialConfig.initial_weight);
  const birth_date = useSelector((s) => s.initialConfig.birth_date);

  const [day, setDay] = useState(null);
  const [month, setMonth] = useState(null);
  const [year, setYear] = useState(null);
  const [dobWheelOpen, setDobWheelOpen] = useState(false);

  // Tracks whether the user has deliberately typed in "Peso di partenza".
  // Until they do, the field auto-mirrors "Peso attuale" so the common
  // "I'm starting today" case fills both fields with one input. We init
  // it to true if redux already has a value (existing users revisiting),
  // so editing Peso attuale never silently clobbers their saved baseline.
  const [pesoPartenzaTouched, setPesoPartenzaTouched] = useState(
    () => !!initial_weight
  );

  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  const birthDateValue = buildBirthDate(day, month, year);

  // Sex and date of birth are required. Height and weights are optional since
  // the client's changes of 14/09/2026, and are checked only when typed.
  const canProceed =
    Boolean(sex) && Boolean(birthDateValue || (birth_date ? String(birth_date) : ""));

  // Hydrate dd/mm/yy from existing birth_date if present
  useEffect(() => {
    if (!birth_date) return;
    const match = String(birth_date).match(/(\d{4})-(\d{2})-(\d{2})/);
    if (!match) return;
    setYear(Number(match[1]));
    setMonth(Number(match[2]));
    setDay(Number(match[3]));
  }, [birth_date]);

  const onPickSex = (value) => {
    setError("");
    dispatch(setInitialConfigProperty({ key: "sex", value })); // "female" | "male"
  };

  // A member must be 18 (client, 14/09/2026): the latest birth date is today
  // 18 years ago, and the wheel opens there. The wheel itself ends with that
  // year, so its day and month turn freely in any order; the exact date is
  // checked on SUCCESSIVO.
  const dobMax = useMemo(() => getMinimumLegalAgeDate(), []);
  const dobWheelMax = useMemo(() => getBirthDateWheelMax(), []);

  // Leaving the screen with the wheel still open must not leave its sheet over
  // the next one.
  useIonViewWillLeave(() => setDobWheelOpen(false));

  const openDobWheel = () => {
    setError("");
    setDobWheelOpen(true);
  };

  // No Conferma: the date is taken as soon as the wheel turns. Nothing is
  // chosen until the member moves it.
  const onDobWheelChange = (value) => {
    const picked = String(Array.isArray(value) ? value[0] : value ?? "").match(
      /(\d{4})-(\d{2})-(\d{2})/
    );
    if (!picked) return;
    setYear(Number(picked[1]));
    setMonth(Number(picked[2]));
    setDay(Number(picked[3]));
    setError("");
  };

  const onChangeCurrentWeight = (v) => {
    setError("");
    // keep as string in store; convert when sending
    dispatch(setInitialConfigProperty({ key: "current_weight", value: v }));
    // Mirror into "Peso di partenza" until the user types there directly.
    if (!pesoPartenzaTouched) {
      dispatch(setInitialConfigProperty({ key: "initial_weight", value: v }));
    }
  };

  const onChangeInitialWeight = (v) => {
    setError("");
    if (!pesoPartenzaTouched) setPesoPartenzaTouched(true);
    dispatch(setInitialConfigProperty({ key: "initial_weight", value: v }));
  };

  const onChangeHeight = (v) => {
    setError("");
    dispatch(setInitialConfigProperty({ key: "height", value: v }));
  };

  const handleNext = async () => {
    setError("");
    const birthDate = buildBirthDate(day, month, year);

    if (!sex) return setError("Seleziona Donna o Uomo.");
    if (!birthDate) return setError("Scegli la tua data di nascita.");
    // The wheel lets any day and month through (see dobWheelMax).
    if (birthDate > dobMax) return setError("Devi avere almeno 18 anni.");

    // A value the member did type must still be a real one. Said in
    // centimetres because that is the unit on the field: someone who typed
    // 1,75 has already had it read as 175 and never sees the height message.
    const measureProblem =
      (isFilled(height) && heightError(height)) ||
      (isFilled(current_weight) && weightError(current_weight, "Il peso attuale")) ||
      (isFilled(initial_weight) && weightError(initial_weight, "Il peso di partenza"));
    if (measureProblem) return setError(measureProblem);

    const h = isFilled(height) ? toCentimetres(height) : null;
    const wCurrent = isFilled(current_weight) ? toKilograms(current_weight) : null;
    const wInitial = isFilled(initial_weight) ? toKilograms(initial_weight) : null;

    setSaving(true);
    try {
      // Optional measures are sent only when given, so an empty box never
      // overwrites a value saved earlier.
      const payload = { sex, birth_date: birthDate };
      if (h !== null) payload.height = h;
      // Peso di partenza (immutable starting baseline).
      if (wInitial !== null) payload.initial_weight = wInitial;
      // Peso attuale — backend seeds a measure-diary row for today.
      if (wCurrent !== null) payload.current_weight = wCurrent;

      if (!isPendingRegistration) {
        await dispatch(
          updateMasterClubSubscriber({ id: 1, method: "PUT", payload })
        ).unwrap();
      }

      // ✅ always keep redux in sync (use birthDate, not birth_date)
      dispatch(setInitialConfigProperty({ key: "sex", value: sex }));
      dispatch(
        setInitialConfigProperty({ key: "height", value: h !== null ? String(h) : "" })
      );
      dispatch(
        setInitialConfigProperty({
          key: "initial_weight",
          value: wInitial !== null ? String(wInitial) : "",
        })
      );
      dispatch(
        setInitialConfigProperty({
          key: "current_weight",
          value: wCurrent !== null ? String(wCurrent) : "",
        })
      );
      dispatch(
        setInitialConfigProperty({ key: "birth_date", value: birthDate })
      );
      trackFunnel("onboarding_step_completed", { step: 1 });
      history.replace("/impostazioni-iniziali/2");
    } catch (e) {
      setError(e?.message ?? "Ops! C'è stato un errore. Riprova.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <IonPage className="ds-init">
      <IonContent fullscreen scrollY={false} className="ds-init__content">
        <div
          className="ds-init__bg"
          style={{ backgroundImage: `url('${bgImage}')` }}
          aria-hidden="true"
        />

        <div className="ds-init__wrap">
          {/* Top progress bar (step 1 of 3) */}
          <div className="ds-init__progress" aria-hidden="true">
            <span className="is-active" />
            <span />
            <span />
          </div>

          <div className="ds-init__title">
            Iniziamo! Ci serve qualche <br />
            informazione per attivare il tuo <br />
            percorso.
          </div>

          {/* Gender */}
          <div className="ds-init__section">
            <div className="ds-init__genderRow">
              <button
                type="button"
                className={`ds-init__checkBtn ${
                  sex === "female" ? "is-on" : ""
                }`}
                onClick={() => onPickSex("female")}
              >
                <span className="ds-init__box" aria-hidden="true" />
                <span className="ds-init__checkLabel">DONNA</span>
              </button>

              <button
                type="button"
                className={`ds-init__checkBtn ${sex === "male" ? "is-on" : ""}`}
                onClick={() => onPickSex("male")}
              >
                <span className="ds-init__box" aria-hidden="true" />
                <span className="ds-init__checkLabel">UOMO</span>
              </button>
            </div>
          </div>

          <div className="ds-init__divider" />

          {/* Birth date */}
          <div className="ds-init__section">
            <div className="ds-init__rowLabel">DATA DI NASCITA</div>

            <div className="ds-init__dobRow">
              <button type="button" className="ds-init__dobSelect" onClick={openDobWheel}>
                {day ? pad2(day) : "Giorno"}
              </button>

              <button type="button" className="ds-init__dobSelect" onClick={openDobWheel}>
                {month ? titleCase(MONTHS_IT[Number(month) - 1]) : "Mese"}
              </button>

              <button type="button" className="ds-init__dobSelect" onClick={openDobWheel}>
                {year ? String(year) : "Anno"}
              </button>
            </div>
          </div>

          <IonModal
            isOpen={dobWheelOpen}
            className="ds-init__dobSheet"
            initialBreakpoint={1}
            breakpoints={[0, 1]}
            onDidDismiss={() => setDobWheelOpen(false)}
          >
            <div className="ds-init__dobSheetBody">
              <div className="ds-init__rowLabel">DATA DI NASCITA</div>
              <IonDatetime
                presentation="date"
                preferWheel
                locale="it-IT"
                min="1930-01-01"
                max={dobWheelMax}
                value={birthDateValue || dobMax}
                onIonChange={(e) => onDobWheelChange(e.detail.value)}
              />
            </div>
          </IonModal>

          <div className="ds-init__divider" />

          {/* Height + weight. Order matches the design mock:
              Altezza -> Peso attuale -> Peso di partenza. */}
          <div className="ds-init__section">
            <div className="ds-init__fieldRow">
              <div className="ds-init__fieldLabel">
                ALTEZZA <span className="ds-init__optional">(facoltativo)</span>
              </div>
              <div className="ds-init__field">
                <input
                  className="ds-init__textInput"
                  value={height ?? ""}
                  onChange={(e) => onChangeHeight(e.target.value)}
                  inputMode="decimal"
                  placeholder="Esempio: 167"
                />
                <span className="ds-init__unit">CM</span>
              </div>
            </div>

            <div className="ds-init__fieldRow">
              <div className="ds-init__fieldLabel">
                PESO ATTUALE <span className="ds-init__optional">(facoltativo)</span>
              </div>
              <div className="ds-init__field">
                <input
                  className="ds-init__textInput"
                  value={current_weight ?? ""}
                  onChange={(e) => onChangeCurrentWeight(e.target.value)}
                  inputMode="decimal"
                  placeholder="Esempio: 78"
                />
                <span className="ds-init__unit">KG</span>
              </div>
            </div>

            <div className="ds-init__fieldRow">
              <div className="ds-init__fieldLabel">
                PESO DI PARTENZA <span className="ds-init__optional">(facoltativo)</span>
              </div>
              <div className="ds-init__field">
                <input
                  className="ds-init__textInput"
                  value={initial_weight ?? ""}
                  onChange={(e) => onChangeInitialWeight(e.target.value)}
                  inputMode="decimal"
                  placeholder="Esempio: 78"
                />
                <span className="ds-init__unit">KG</span>
              </div>
            </div>

            <IonText className="ds-init__hint">
              Inserisci il peso con cui hai iniziato il tuo percorso Dieta
              Social. Se inizi oggi, usa lo stesso valore del peso attuale.
            </IonText>

            {error ? <div className="ds-init__error">{error}</div> : null}
          </div>

          <IonLoading isOpen={saving} message="Attendere prego..." />
        </div>
      </IonContent>

      <IonFooter className="ds-init__footer--fixed">
        <div className="ds-init__footer">
          <IonButton
            className="ds-init__next"
            color="primary"
            expand="block"
            onClick={handleNext}
            disabled={!canProceed || saving}
          >
            SUCCESSIVO
          </IonButton>
        </div>
      </IonFooter>
    </IonPage>
  );
};

export default PhysicalPersonalStep;
